import React from 'react';
import { TrendingUp, CheckCircle, Award, Sun, Zap } from 'lucide-react';

const ICONS = { TrendingUp, CheckCircle, Award, Sun, Zap };

const DEFAULT_SOLUTIONS = [
  { title: 'Cost Efficiency', description: 'Reduce operational costs with long-term energy savings and predictable pricing. Achieve significant ROI through optimized renewable energy solutions.', icon: 'TrendingUp' },
  { title: 'Sustainability', description: 'Meet your ESG goals and corporate sustainability commitments. Build brand value through environmental responsibility and clean energy adoption.', icon: 'CheckCircle' },
  { title: 'Competitive Edge', description: 'Stay ahead of regulatory requirements and market trends. Demonstrate industry leadership through early adoption of renewable energy solutions.', icon: 'Award' },
];

const DEFAULT_CONSULTATION = [
  { title: 'Right Renewable Solution', description: 'Navigate complex options with expert guidance. We analyze your energy needs, site constraints, and business objectives to recommend the optimal renewable energy solution.', color: 'emerald' },
  { title: 'Right Renewable Partner', description: 'Access our vetted network of developers and EPC companies. We connect you with reliable partners who deliver quality projects on time and within budget.', color: 'blue' },
  { title: 'Right Renewable Terms', description: 'Negotiate favorable commercial and contractual terms. Our legal and commercial expertise ensures you secure competitive pricing and protect your interests.', color: 'emerald' },
];

const DEFAULT_SWITCH_NOW = [
  { title: 'First Mover Advantage', points: ['Be the first in your industry showcasing a competitive edge', 'Ensure long-term economic benefits', 'Inspire other businesses to follow your lead'], color: 'emerald' },
  { title: 'Favourable Policies', points: ['Tariffs at the lowest and will increase in future', 'Attractive state regulations available now', 'Capitalize on government incentives and subsidies'], color: 'blue' },
  { title: 'Growing Demand', points: ['Secure your renewable energy capacity now', 'Gain access to premier solar parks and projects', 'Lock in competitive pricing before demand peaks'], color: 'yellow' },
];

const SectionHeading = ({ heading, highlight, light, highlightClass = 'text-emerald-600' }) => (
  <div className="text-center mb-16">
    <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${light ? 'text-white' : ''}`}>
      {heading} <span className={highlightClass}>{highlight}</span>
    </h2>
  </div>
);

export const WhySolutionsSection = ({ section }) => (
  <section className="py-20 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading heading={section?.heading || 'Why Buy Renewable Energy'} highlight={section?.headingHighlight || 'Solutions?'} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {(section?.cards || DEFAULT_SOLUTIONS).map((card, idx) => {
          const CardIcon = ICONS[card.icon] || TrendingUp;
          const even = idx % 2 === 0;
          return (
            <div key={card.title} className="bg-white rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className={`w-24 h-24 ${even ? 'bg-emerald-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <CardIcon className={`w-12 h-12 ${even ? 'text-emerald-600' : 'text-blue-600'}`} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{card.title}</h3>
              <p className="text-gray-600 text-center leading-relaxed">{card.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export const WhyConsultationSection = ({ section }) => (
  <section className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading heading={section?.heading || 'Why Buy Renewable Energy'} highlight={section?.headingHighlight || 'Consultation?'} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {(section?.cards || DEFAULT_CONSULTATION).map((card, idx) => (
          <div key={card.title} className={`bg-gradient-to-br ${idx % 2 === 0 ? 'from-emerald-50 to-emerald-100' : 'from-blue-50 to-blue-100'} rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{card.title}</h3>
            <p className="text-gray-700 leading-relaxed">{card.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CHECK_COLORS = { emerald: 'text-emerald-300', blue: 'text-blue-300', yellow: 'text-yellow-300' };

export const WhySwitchNowSection = ({ section }) => (
  <section className="py-20 bg-gradient-to-br from-emerald-700 via-teal-700 to-blue-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading light highlightClass="text-yellow-300" heading={section?.heading || 'Why Switch to Renewable Energy'} highlight={section?.headingHighlight || 'Now?'} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {(section?.cards || DEFAULT_SWITCH_NOW).map((card, idx) => {
          const checkColor = CHECK_COLORS[card.color] || CHECK_COLORS[['emerald', 'blue', 'yellow'][idx % 3]];
          return (
            <div key={card.title} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-10 shadow-xl hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2">
              <h3 className="text-2xl font-bold text-white mb-6">{card.title}</h3>
              <div className="space-y-4">
                {(card.points || []).map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle className={`w-6 h-6 ${checkColor} flex-shrink-0 mt-1`} />
                    <p className="text-white/90">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);
